import { useEffect, useRef } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { ClaimFlowLayout } from './components/ClaimFlowLayout';
import { ClaimFlowAuthGuard } from './components/ClaimFlowAuthGuard';
import { DashboardPage } from './pages/DashboardPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { ConfigVersioningPage } from './pages/ConfigVersioningPage';
import { DocumentsPopoutPage } from './pages/DocumentsPopoutPage';
import { IntegratedComponentProvider } from '@components/integrated/shared';
import type { ThemeOverrideConfig } from '@components/integrated/theme-override';
import { claimflowApiClient } from './api/claimflowApiClient';
import { claimflowAuthService } from './api/authService';
import { useClaimFlowAuthStore } from './auth/store';
import { registerClaimflowComponents } from './components/register';

// Register claimflow's configurable components with the shared registry
// once, at module import time. `registerClaimflowComponents()` is
// idempotent so HMR and test re-imports remain safe.
registerClaimflowComponents();

/**
 * Fetches the brand-theme config from GET /claimflow/config and applies
 * CSS custom properties directly on :root so they override the defaults
 * for all Tailwind utility classes.
 */
function useApplyBrandTheme() {
  const applied = useRef(false);
  // The route is mounted as /claimflow/:tenantSlug/*, so the slug is the
  // source of truth for which tenant's branding to load. It is passed
  // explicitly to getConfig so the (anonymous, pre-login) config fetch wins
  // over any stale localStorage tenant.
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  useEffect(() => {
    if (applied.current) return;
    applied.current = true;

    claimflowAuthService.getConfig(tenantSlug)
      .then((res) => {
        // The (anonymous) config payload is not guaranteed to carry `config`:
        // read it as optional rather than trusting the declared shape.
        const body = res.data as { config?: Record<string, unknown> } | undefined;
        const theme = body?.config?.['brand-theme'] as ThemeOverrideConfig | undefined;
        if (!theme) return;

        const root = document.documentElement;

        // Apply color overrides
        if (theme.colors) {
          const palettes = Object.entries(theme.colors) as [
            string,
            Partial<Record<string, string>> | undefined,
          ][];
          for (const [palette, scale] of palettes) {
            if (!scale) continue;
            for (const [shade, value] of Object.entries(scale)) {
              if (typeof value === 'string') {
                root.style.setProperty(`--color-${palette}-${shade}`, value);
              }
            }
          }
        }

        // Apply typography overrides
        if (theme.typography?.fontSans) {
          root.style.setProperty('--font-sans', theme.typography.fontSans);
        }
        if (theme.typography?.fontMono) {
          root.style.setProperty('--font-mono', theme.typography.fontMono);
        }

        // Apply border radius overrides
        if (theme.borderRadius) {
          const radiusMap: Record<string, string> = {
            sm: '--radius-sm', DEFAULT: '--radius', md: '--radius-md',
            lg: '--radius-lg', xl: '--radius-xl', '2xl': '--radius-2xl',
          };
          for (const [key, value] of Object.entries(theme.borderRadius)) {
            const cssVar = radiusMap[key];
            if (cssVar && typeof value === 'string') {
              root.style.setProperty(cssVar, value);
            }
          }
        }

        // Apply shadow overrides
        if (theme.shadows) {
          const shadowMap: Record<string, string> = {
            sm: '--shadow-sm', DEFAULT: '--shadow', md: '--shadow-md',
            lg: '--shadow-lg', xl: '--shadow-xl',
          };
          for (const [key, value] of Object.entries(theme.shadows)) {
            const cssVar = shadowMap[key];
            if (cssVar && typeof value === 'string') {
              root.style.setProperty(cssVar, value);
            }
          }
        }
      })
      .catch(() => {
        // Config fetch failure (incl. 404 TENANT_NOT_FOUND for a bad slug)
        // should not block the UI — fall back to the default theme.
      });
    // `applied` guards run-once; tenantSlug is listed only to satisfy the
    // exhaustive-deps rule and is stable for a given route.
  }, [tenantSlug]);
}

export function ClaimFlowRoutes() {
  const activeTenantId = useClaimFlowAuthStore((s) => s.activeTenantId);
  useApplyBrandTheme();

  return (
    <ClaimFlowAuthGuard>
      <IntegratedComponentProvider
        apiClient={claimflowApiClient}
        tenant={activeTenantId ?? ''}
        module="claimflow"
      >
        {/* `ClaimFlowLayout` now wraps each main route individually so the bare
            popout route renders without the app header/chrome. React reconciles
            the same-type `ClaimFlowLayout` across dashboard↔task↔config, so it
            stays mounted between those exactly as before. */}
        <Routes>
          <Route path="/" element={<ClaimFlowLayout><DashboardPage /></ClaimFlowLayout>} />
          <Route
            path="/task/:taskId"
            element={<ClaimFlowLayout><TaskDetailPage /></ClaimFlowLayout>}
          />
          {/* Claim screen WITHOUT a task (`/task?id_sin=…[&task=…][&ses=…]`) —
              opened in a new tab from the claim search (mahhp 22861). */}
          <Route
            path="/task"
            element={<ClaimFlowLayout><TaskDetailPage /></ClaimFlowLayout>}
          />
          <Route
            path="/config"
            element={<ClaimFlowLayout><ConfigVersioningPage /></ClaimFlowLayout>}
          />
          <Route path="/task/:taskId/documents-popout" element={<DocumentsPopoutPage />} />
          {/* Documents popout of a task-less claim screen (mahhp 22861). */}
          <Route path="/documents-popout" element={<DocumentsPopoutPage />} />
        </Routes>
      </IntegratedComponentProvider>
    </ClaimFlowAuthGuard>
  );
}
